import { decorate, action } from "mobx"
import autoBind from "auto-bind"
import axios from "axios";

import ApiClient from "utils/ApiClient";

import {
  FIELD_TYPES_BY_FIELD_TYPE,
  FIELD_TYPES,
} from "../lib/constants";

import { prepareSheetSchema, removeQuotes, base64UrlDecode, makeKey, getHeaders, formatList, keyBy } from "../lib/utils";

import { handleSocketConnection } from "app/websocketManager/eventEmitter";

// pack icon imports
import { iconsSystemMessage, packIconsFunc } from "../configs/icons.config";
import RequestLimitError from "../classes/RequestLimitError";

const formula_types = {
  text: "1",
  number: "5",
  date: "4",
  currency: "17",
  dateTime: "16",
};

class ChatHelpersStore {

  constructor(appStore) {
    this.appStore = appStore;
    autoBind(this)
  }

  async getChatCompletion(url, body, options = { headers: {} }) {
    const res = await axios.post(url, body, {
      headers: options.headers,
    });

    if (res.status === 429 || res.status === 422) {
      const headers = getHeaders(res.headers);
      throw new RequestLimitError(res.data?.message, res.status, headers, body.model);
    }

    return res;
  }

  async sendAdminNotification(body) {
    try {
      await ApiClient.Assistant.notifyAdmin(body);
    } catch (e) {
      console.log(`e->`, e);
    }

  }

  parseOpenAIFnResponse(data) {
    const { choices = [] } = data;
    if (!choices.length) {
      throw new Error();
    }

    const [
      {
        message: {
          function_call: { arguments: args },
        },
      },
    ] = choices;

    if (!args) {
      throw new Error();
    }

    return { args: JSON.parse(args) }
  }

  /** 
   * modified copy of ContainerStore.loadGrid() function
   */
  async loadGridByAssistant(sheetId) {
    const containerStore = this.appStore.containerStores[sheetId];
    await containerStore.fetchListConfig();
    await containerStore.fetchViews(false, true);
    // await containerStore.fetchView(containerStore.view.id, true);
    containerStore.notificationsStore.fetchData();
    containerStore.remindersStore.fetchData();
  }

  async updateSheetPackAfterUpdate({ sheetId, packId }) {
    /**
     * 1. fetch packs
     * 2. update the leftOverMenuStore Packs
     * 3. if user is on current pack then fetch all sheets (via loadPackLists(packId) referenced from handlePackIdChange)
     * 4. add to recent packs
     * 5. Navigate to targeted pack and sheet
     */
    const res = await this.appStore.assistantStore.fetchAndLoadPacks(); //1
    if (!res || !res.data) throw new Error("Unable to fetch packs");

    const packs = [...res.data];
    this.appStore.leftHoverMenuStore.setPacks(packs); //2

    if (this.appStore.packStore.id === packId) {
      await this.appStore.leftHoverMenuStore.loadPackLists(packId); //3
    }
    for (const existingSheet of this.appStore.packStore.lists) {
      if (existingSheet.id == sheetId && this.appStore.containerStores[sheetId]) {
        await this.loadGridByAssistant(sheetId);
        break;
      }
    }

    await this.appStore.leftHoverMenuStore.addToRecentPacks(packId); // 4
  }

  navigateToSheet({ sheetId, packId }) {
    this.appStore.routerHistory.push(`/pack/${packId}/list/${sheetId}`);
    handleSocketConnection(sheetId);
  }

  async createSheetApiHelper({
    sheetName,
    singularNameOfSheet,
    fields,
    pack_id,
  }) {

    const { list_name, columns } = prepareSheetSchema({
      fields,
      sheetName,
    });

    //1. create the sheet
    const res = await this.appStore.assistantStore.createSheet(list_name);
    const list_id = res.data?.id;

    //2. add sheet to current pack
    await this.appStore.assistantStore.addSheetToPack({ list_id, pack_id });

    //3. add all columns to the sheet
    const columnsRes = await this.appStore.assistantStore.addColumnsToSheet({
      columns: columns.map((column) => {
        column.options = JSON.stringify([column.options]);
        column.column_id = 0;
        return column;
      }),
      list_name,
      list_id,
      add_button_text: `New ${singularNameOfSheet || "Entry"}`,
    });

    return {
      columns,
      list_name,
      list: res.data,
      columnsData: columnsRes.data,
    };
  }

  async createSubsheetApiHelper({
    fields,
    sheetName,
    parentSheetId,
  }) {

    const { list_name, columns } = prepareSheetSchema({
      fields: fields,
      sheetName,
    });

    // Create subsheet with general fields
    const res = await this.appStore.assistantStore.createList({
      parent_id: parentSheetId,
      list_name,
      columns: columns.map((column) => {
        column.column_id = 0;
        column.options = JSON.stringify([column.options]);
        return column;
      }),
      add_button_text: "New Entry",
    });

    return res;
  }

  async addFieldsToSheetApiHelper({
    fields,
    sheetId,
    sheetName,
    parentSheetId
  }) {
    try {
      const { list_name, columns } = prepareSheetSchema({
        fields,
        sheetName,
      });

      const res = await this.appStore.assistantStore.addColumnsToSheet({
        columns: columns.map((column) => {
          column.options = JSON.stringify([column.options]);
          column.column_id = 0;
          return column;
        }),
        list_name,
        list_id: sheetId,
        parent_id: parentSheetId
      });

      return {
        ...res.data,
        sheetId,
        parentSheetId,
        sheetName,
        fields
      }

    } catch (e) {
      console.log(`e->`, e);
    }

  }

  async generateFormulaColumnSchema(
    formula,
    createdColumns,
    sheetId
  ) {
    try {
      let expression = formula.attributes?.formula;
      let formula_type = formula.attributes?.formula_type;
      let col_map = {};

      for (let column of createdColumns) {
        if (column.column_type_id != 6) {
          col_map[column.name] = column.alias_path;
        } else {
          column.list_columns?.forEach((el) => {
            col_map[el.name] = column.alias_path + "." + el.alias_name;
            col_map[column.name + "." + el.name] =
              column.alias_path + "." + el.alias_name;
          });
        }
      }

      for (let key in col_map) {
        expression = expression.replaceAll("%" + key + "%", col_map[key]);
      }

      expression = removeQuotes(expression);

      let validate = await ApiClient.List.validateFilterQuery(sheetId, expression);
      let defaultOptions = [
        {
          is_read_only: false,
          is_mandatory: false,
          is_unique: false,
          unique_options: null,
          is_filter: false,
          trim_white_spaces: true,
          formula: validate.success ? expression : "'" + expression + "'",
          formula_type: formula_type,
          formula_column: {
            options: JSON.stringify([{}]),
            column_type_id: formula_types[formula_type],
          },
          apply_formula: true
        },
      ];

      let defaultSchema = {
        column_id: 0,
        column_name: formula.field_name,
        column_type_id: 19,
        formula: "",
        options: defaultOptions,
        is_deleted: false,
      };

      return defaultSchema;
    } catch (e) {
      console.log(`e->`, e);
      return;
    }
  }

  // Function defined for get icon from chat gpt
  async getIcon({ packName }) {
    try {
      let context = iconsSystemMessage;
      let functions = packIconsFunc;

      let msgs = context;
      msgs = [
        ...msgs,
        {
          role: "user",
          content: `get appropriate material ui icon name and color for ${packName}`,
          type: "message",
        },
      ];

      const body = {
        frequency_penalty: 0,
        max_tokens: 1024,
        model: "gpt-3.5-turbo-16k",
        temperature: 1,
        top_p: 1,
        presence_penalty: 0,
        messages: msgs
          .filter(
            (message) => message.type === "message" || message.type === "internal"
          )
          .map((message) => {
            if (message.role == "function") {
              return {
                role: message.role,
                content: message.content,
                name: message.name,
              };
            }
            return {
              role: message.role,
              content: message.content,
            };
          }),
        functions: functions,
        function_call: { name: "getMuiIcon" },
        stream: false,
      };

      const accessToken = await this.appStore.assistantStore.getChatsAccessToken()

      const res = await this.getChatCompletion(this.appStore.chatSettingsStore.apiEndpoints.chatCompletionUrl, body, {
        headers: { Authorization: `Bearer ${accessToken?.token}` },
      })

      return this.parseOpenAIFnResponse(res.data)?.args;
    } catch (error) {
      console.log(error);
      return;
    }
  };


  async getOrCreatePack({ packName = "" }) {

    const _packName = packName.trim();
    const pack = this.appStore.assistantStore.getPackByName(_packName);
    if (pack) return { pack };

    const res = await this.appStore.assistantStore.savePack({
      name: _packName,
      createDefaultSheet: 0,
    });

    if (!res || !res.data) {
      throw new Error(`Unable to create pack with name: "${_packName}".`);
    }

    return { pack: res.data, isNew: true };
  };

  getSheetFieldsInfoRecursively(fields = []) {
    return fields.map((field) => {
      if (field.list_columns && field.list_columns.length) {
        return {
          [`${field.name} (id: ${field.id})`]: this.getSheetFieldsInfoRecursively(
            field.list_columns
          ),
        };
      }

      return `${field.name} (id: ${field.id})`;
    });
  };

  async getChatConfig() {
    if (this.appStore.chatSettingsStore.chatConfig)
      return this.appStore.chatSettingsStore.chatConfig;

    const accessToken = await this.appStore.assistantStore.getChatsAccessToken()


    const res = await axios.get(this.appStore.chatSettingsStore.apiEndpoints.getChatsConfig, {
      headers: {
        Authorization: `Bearer ${accessToken?.token}`,
      },
    });
    if (res.data && res.data) {
      this.appStore.chatSettingsStore.setChatConfig(res.data);
    }
  };

  async getChatsAccessTokenHelper() {
    const resData = await ApiClient.Assistant.getChatsAccessToken()
    if (!resData || !resData.token)
      throw new Error("Unable to get access for chats");

    const encodedPayload = resData.token.split(".")[1];
    const decoded = JSON.parse(base64UrlDecode(encodedPayload));
    const accessToken = { token: resData.token, expires: decoded.exp };
    return accessToken;
  };

  replaceLookupSheetNames(fields, namesMap) {
    const newFields = [];

    for (const field of fields) {
      if (field.field_type_id != FIELD_TYPES.lookup) {
        newFields.push(field);
        continue;
      }

      let { attributes = {} } = field;
    
      attributes = typeof attributes === "object" ? attributes : {};

      if (!attributes.source_sheet_name)
        attributes.source_sheet_name = "";
      if (!attributes.source_column_name)
        attributes.source_column_name = "";
      // get the updated name of the sheet
      const srcSheetName =
        namesMap[attributes.source_sheet_name] || attributes.source_sheet_name;

      // check if the sheet exists
      const srcSheet = this.appStore.assistantStore.getSheetByName(srcSheetName);

      // if src sheet doesn't exist, append it
      // so manual lookup configuration
      if (!srcSheet) {
        newFields.push(field);
        continue;
      }

      // append the source sheet id to the field definition
      newFields.push({
        ...field,
        attributes: {
          ...field.attributes,
          source_sheet_name: srcSheetName,
          source_list_id: srcSheet.id,
        },
      });
    }

    return newFields;
  };

  async handlePacksTaskComplete(packs) {
    this.appStore.chatTaskStore.setPacks(packs);

    for (const pack of packs) {
     await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `${pack.isNew ? "Created" : "Using"} pack: "${pack.name
          }" with id: "${pack.id}"`,
      });
    }
    // move to next task
    this.appStore.chatTaskStore.moveToNextTask();
  };

  async handleSheetsTaskComplete(sheets, newNamesMap = {}) {
    this.appStore.chatTaskStore.setSheets(sheets);
    this.appStore.chatTaskStore.setSheetsNewNames(newNamesMap || {});

    for (const sheet of sheets) {
      const { suggestedSheetName, sheetName, id } = sheet;
      await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `Your sheet: "${sheetName}"${suggestedSheetName
          ? ` (earlier suggested was "${suggestedSheetName}")`
          : ""
          } with id: "${id}" has been created successfully.`,
      });
    }

    // move to next task
    this.appStore.chatTaskStore.moveToNextTask();
  };

  async handleGetSubsheetsTaskParams() {
    await this.appStore.assistantStore.fetchAndLoadSheets();

    const subsheets = [];

    for (const subsheet of this.appStore.chatTaskStore.subsheets) {
      const { parentSheetName } = subsheet;
      if (!parentSheetName) return;

      let updatedParentSheetName =
        this.appStore.chatTaskStore.newSheetNames[parentSheetName] || parentSheetName;

      const parentSheet = this.appStore.assistantStore.getSheetByName(updatedParentSheetName);

      // if parent sheet doesn't exist, skip this item
      if (!parentSheet) {
        continue;
      }

      subsheets.push({
        ...subsheet,
        parentSheetName: updatedParentSheetName,
        parentSheetId: parentSheet.id,
      });
    }

    this.appStore.chatTaskStore.setSubsheets(subsheets);
    return subsheets;
  };

  async handleSubsheetsTaskComplete(subsheets) {
    this.appStore.chatTaskStore.setSubsheets(subsheets);

    const subsheetsByParentSheetId = {};

    for (const subsheet of subsheets) {
      const { parentSheetId } = subsheet;
      const parentSheet = this.appStore.assistantStore.sheetsById[parentSheetId];
      if (!parentSheet) continue;

      if (!subsheetsByParentSheetId[parentSheetId]) {
        subsheetsByParentSheetId[parentSheetId] = [];
      }
      subsheetsByParentSheetId[parentSheetId].push(subsheet);
    }

    for (const parentSheetId in subsheetsByParentSheetId) {
      const subsheets = subsheetsByParentSheetId[parentSheetId];

      if (!subsheets.length) continue;

      const parentSheet = this.appStore.assistantStore.sheetsById[parentSheetId];

      await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `Added ${formatList(
          subsheets.map((subsheet) => `"${subsheet.sheetName}"`)
        )} subsheet(s) into "${parentSheet.name}" sheet.`,
      });
    }

    // move to next task
    this.appStore.chatTaskStore.moveToNextTask();
  };

  getFieldFromTask(field, { subheetsByKey }) {
    const { sheetName, parentSheetName } = field;

    if (parentSheetName) {
      let updatedParentSheetName =
        this.appStore.chatTaskStore.newSheetNames[parentSheetName] || parentSheetName;

      const parentSheet = this.appStore.assistantStore.getSheetByName(updatedParentSheetName);

      // if parent sheet doesn't exist, skip this item
      if (!parentSheet) return;

      const key = makeKey(parentSheet.id + "_" + sheetName);

      const subsheet = subheetsByKey[key];

      if (!subsheet) return;

      return {
        ...field,
        parentSheetName: updatedParentSheetName,
        parentSheetId: parentSheet.id,
        sheetId: subsheet.list_id,
        sheetName,
      };
    } else {
      let updatedSheetName =
        this.appStore.chatTaskStore.newSheetNames[sheetName] || sheetName;

      const sheet = this.appStore.assistantStore.getSheetByName(updatedSheetName);

      // if sheet doesn't exist, skip this item
      if (!sheet) return;

      return {
        ...field,
        sheetId: sheet.id,
        sheetName: updatedSheetName,
      };
    }
  };

  async getFieldsByFieldTypeFromTask(fieldType) {
    await this.appStore.assistantStore.fetchAndLoadSheets();
    if (!this.appStore.chatTaskStore.fieldsByType || !this.appStore.chatTaskStore.fieldsByType[fieldType])
      return;

    const fields = [];

    const subheetsByKey = keyBy(this.appStore.chatTaskStore.subsheets || [], (item) =>
      makeKey(item.parent_id + "_" + (item.name || item.sheetName))
    );

    for (const field of this.appStore.chatTaskStore.fieldsByType[fieldType]) {
      const preparedField = this.getFieldFromTask(field, { subheetsByKey });
      if (preparedField) {
        fields.push(preparedField);
      }
    }

    return fields;
  };

  async handleGetAddGeneralFieldsTaskParams() {
    const generalFields = [];
    const subheetsByKey = keyBy(this.appStore.chatTaskStore.subsheets || [], (item) =>
      makeKey(item.parent_id + "_" + (item.name || item.sheetName))
    );

    for (const fieldType in this.appStore.chatTaskStore.fieldsByType) {
      if (
        fieldType == FIELD_TYPES.lookup ||
        fieldType == FIELD_TYPES.formula ||
        fieldType == FIELD_TYPES.subsheet
      )
        continue;

      const fields = this.appStore.chatTaskStore.fieldsByType[fieldType];

      generalFields.push(
        ...fields
          .map((field) => this.getFieldFromTask(field, { subheetsByKey }))
          // remove fields becoming undefined
          .filter((field) => field)
      );
    }

    return generalFields;
  };

  async handleAddGeneralFieldsTaskComplete(fieldPerSheet) {
    // group fields by their fieldType
    for (const {
      fields,
      column,
      column_id,
      list_columns,
      ...restItem
    } of fieldPerSheet) {
      // group all fields by their field type
      const fieldsByFieldType = {};
      for (const field of fields) {
        if (!fieldsByFieldType[field.field_type_id]) {
          fieldsByFieldType[field.field_type_id] = [];
        }
        fieldsByFieldType[field.field_type_id].push(field);
      }

      for (const fieldType in fieldsByFieldType) {
        // save grouped fields (per sheet) to task store
        this.appStore.chatTaskStore.addFields(
          [
            {
              fields: fieldsByFieldType[fieldType],
              ...restItem,
            },
          ],
          fieldType
        );
      }
    }

    for (const { fields, parentSheetId, sheetName } of fieldPerSheet) {
      if (!fields.length) continue;
      await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `Added ${formatList(
          fields.map(
            (field) =>
              `"${field.field_name} (${FIELD_TYPES_BY_FIELD_TYPE[field.field_type_id]
              })"`
          )
        )} field(s) to "${sheetName}" ${parentSheetId ? "subsheet" : "sheet"}`,
      });
    }

    // move to next task
    this.appStore.chatTaskStore.moveToNextTask();
  };

  async handleGetLookupsTaskParams() {
    const lookups = await this.getFieldsByFieldTypeFromTask(FIELD_TYPES.lookup);
    if (!lookups) return;
    const attachedReferences = lookups.map((lookup) => ({
      ...lookup,
      fields: this.replaceLookupSheetNames(
        lookup.fields,
        this.appStore.chatTaskStore.newSheetNames
      ),
    }));
    this.appStore.chatTaskStore.setFields(attachedReferences, FIELD_TYPES.lookup);
    return attachedReferences;
  };

  async handleLookupsTaskComplete(lookups) {
    const sheetIds = {};
    const subsheetIds = {};

    for (const lookup of lookups) {
      const { sheetId, parentSheetId, sheetName } = lookup;
      if (parentSheetId) {
        if (!subsheetIds[sheetId]) subsheetIds[sheetId] = sheetName;
      } else {
        if (!sheetIds[sheetId]) sheetIds[sheetId] = sheetName;
      }
    }

    for (const sheetId in sheetIds) {
      await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `Added lookup(s) to "${sheetIds[sheetId]}" sheet`,
      });
    }

    for (const sheetId in subsheetIds) {
      await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `Added lookup(s) to "${subsheetIds[sheetId]}" subsheet`,
      });
    }

    // move to next task
    this.appStore.chatTaskStore.moveToNextTask();
  };

  async handleGetFormulaeTaskParams() {
    const formulae = await this.getFieldsByFieldTypeFromTask(FIELD_TYPES.formula);
    if (!formulae) return;
    this.appStore.chatTaskStore.setFields(formulae, FIELD_TYPES.formula);
    return formulae;
  };

  async handleFormulaeTaskComplete(formulae) {
    const sheetIds = {};
    const subsheetIds = {};

    for (const formula of formulae) {
      const { sheetId, parentSheetId, sheetName } = formula;
      if (parentSheetId) {
        if (!subsheetIds[sheetId]) subsheetIds[sheetId] = sheetName;
      } else {
        if (!sheetIds[sheetId]) sheetIds[sheetId] = sheetName;
      }
    }

    for (const sheetId in sheetIds) {
      await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `Added formula(e) to "${sheetIds[sheetId]}" sheet`,
      });
    }

    for (const sheetId in subsheetIds) {
      await this.appStore.chatStore.addChatMessage({
        role: "assistant",
        type: "message",
        content: `Added formula(e) to "${subsheetIds[sheetId]}" subsheet`,
      });
    }

    // move to next task
    this.appStore.chatTaskStore.moveToNextTask();
  };

  async handleGetNavigatorTaskParams() {
    const fieldsBySheetId = {};
    
    // loop through all fields
    for (const fieldType in this.appStore.chatTaskStore.fieldsByType) {
      let sheet, fields;
      for (const field of this.appStore.chatTaskStore.fieldsByType[fieldType]) {
        fields = field;
        if (field.parentSheetName) {
          let updatedSheetName = this.appStore.chatTaskStore.newSheetNames[
            field.parentSheetName
          ] || [field.parentSheetName];

          const parentSheet = this.appStore.assistantStore.getSheetByName(updatedSheetName);

          // if parentSheet doesn't exist, skip this item
          if (!parentSheet) {
            continue;
          }

          if (!fieldsBySheetId[field.sheetId])
            fieldsBySheetId[field.sheetId] = [];

          fieldsBySheetId[field.sheetId].push(...field.fields);

          continue;
        }

        let updatedSheetName = this.appStore.chatTaskStore.newSheetNames[field.sheetName] || [
          field.sheetName,
        ];

        sheet = this.appStore.assistantStore.getSheetByName(updatedSheetName);
      }
      if (sheet !== undefined || !fields.fields) {
        if (!fieldsBySheetId[sheet.id]) fieldsBySheetId[sheet.id] = [];
        fieldsBySheetId[sheet.id].push(...fields.fields);
      }
    }

    // if there are subsheets, then add the as well.
    for (const subsheet of this.appStore.chatTaskStore.subsheets || []) {
      if (!fieldsBySheetId[subsheet.parentSheetId])
        fieldsBySheetId[subsheet.parentSheetId] = [];
      
      fieldsBySheetId[subsheet.parentSheetId].push({
        field_type_id: FIELD_TYPES.subsheet,
        field_name: subsheet.sheetName,
        sheetId: subsheet.list_id,
        ...subsheet,
      });
          }

    const params = [
      {
        pack: this.appStore.chatTaskStore.packs?.[0],
        sheets: this.appStore.chatTaskStore.sheets.map((sheet) => {
          return {
            ...sheet,
            fields: [
              ...(sheet.fields || []),
              ...(fieldsBySheetId[sheet.id] || []).map((field) => {
                if (field.field_type_id != FIELD_TYPES.subsheet) return field;

                const subsheetFields = fieldsBySheetId[field.sheetId];
                if (!subsheetFields) return field;

                let subsheetFieldsName = [];
                let createdSubsheetFields = [];

                [...subsheetFields, ...(field.fields ? field.fields : [])].forEach(el => {
                  if (!subsheetFieldsName.includes(el.field_name)) {
                    createdSubsheetFields.push(el);
                    subsheetFieldsName.push(el.field_name);
                  }
                })

                field.fields = createdSubsheetFields

                return field;
              }),
            ],
          };
        }),
      },
    ];

    return params;
  };

  async handleGetDummyDataTaskParams() {
    const params = [
      {
        sheetIds: this.appStore.chatTaskStore.sheets.map((sheet) => sheet.id),
      },
    ];
    return params;
  };

  async handleGetShareSheetTaskParams() {
    const params = [
      {
        sheetIds: this.appStore.chatTaskStore.sheetIds,
        roles: this.appStore.chatTaskStore.roles,
        emails: this.appStore.chatTaskStore.emails,
      },
    ];
    return params;
  };

  async handleShareSheetTaskComplete(isSuccess = true, message = "") {
    await this.appStore.chatStore.addChatMessage({
      role: "assistant",
      type: "message",
      content: isSuccess ? message === "" ? "Sheet shared successfully" : message : message === "" ? "Failed to share sheet" : message,
    });

    this.appStore.chatTaskStore.moveToNextTask();
  };

  async handleDummyDataTaskComplete() {
    this.appStore.chatTaskStore.moveToNextTask();
  };


}

decorate(ChatHelpersStore, {
  getChatCompletion: action,
  sendAdminNotification: action,
  parseOpenAIFnResponse: action,
  loadGridByAssistant: action,
  updateSheetPackAfterUpdate: action,
  navigateToSheet: action,
  createSheetApiHelper: action,
  createSubsheetApiHelper: action,
  addFieldsToSheetApiHelper: action,
  generateFormulaColumnSchema: action,
  getIcon: action,
  getOrCreatePack: action,
  getSheetFieldsInfoRecursively: action,
  getChatConfig: action,
  getChatsAccessTokenHelper: action,
  replaceLookupSheetNames: action,
  handlePacksTaskComplete: action,
  handleSheetsTaskComplete: action,
  handleGetSubsheetsTaskParams: action,
  handleSubsheetsTaskComplete: action,
  getFieldFromTask: action,
  getFieldsByFieldTypeFromTask: action,
  handleGetAddGeneralFieldsTaskParams: action,
  handleAddGeneralFieldsTaskComplete: action,
  handleGetLookupsTaskParams: action,
  handleLookupsTaskComplete: action,
  handleGetFormulaeTaskParams: action,
  handleFormulaeTaskComplete: action,
  handleGetNavigatorTaskParams: action,
  handleGetDummyDataTaskParams: action,
  handleGetShareSheetTaskParams: action,
  handleShareSheetTaskComplete: action,
  handleDummyDataTaskComplete: action,
})

export default ChatHelpersStore;